import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { formatDate, stringToDate } from "@/components/helpers/DateFormatters";
import Loading from "@/components/Loading";
import HeadSelect from "@/components/table/shared/HeadSelect";
import ModuleTableColumn from "@/components/table/shared/ModuleTableColumn";
import { useMainStore } from "@/contexts/Store";
import {
  ASSESSMENT_TEMPLATE_HEADERS as FIELDS,
  ROUTES,
} from "@/features/risk-assessment";
import { AssessmentIndex } from "@/features/risk-assessment/types/assessment";
import { getCountStringOfItem } from "@/utils/utils";

import { AssessmentAPI } from "../../../api/legacy/risk-assessment/AssessmentApi";
import ViewModuleUsers from "../../../components/dashboard/ViewModuleUsers";
import DashboardContent from "../../../components/shared/DashboardContent";
import DashboardContentWrapper from "../../../components/shared/DashboardContentWrapper";
import DashboardHeader from "../../../components/shared/DashboardHeader";
import ModuleHeader from "../components/ModuleHeader/ModuleHeader";
import RiskAssessmentTabs from "../components/RiskAssessmentTabs";
import CustomCell from "../components/Table/CustomCell";
import CustomTable from "../components/Table/CustomTable";
import AssessmentTemplateButtons from "./AssessmentTemplateButtons";

function AssessmentTemplatesPage() {
  const mainStore = useMainStore();

  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [assessmentTemplates, setAssessmentTemplates] = useState<
    AssessmentIndex[]
  >([]);
  const riskMethodologies = mainStore.riskMethodologies.list || [];
  const { workspaceID } = mainStore.context;

  useEffect(() => {
    setLoading(true);
    if (!workspaceID) {
      return;
    }
    AssessmentAPI.getAll(workspaceID, { templates: true })
      .then(setAssessmentTemplates)
      .catch(() =>
        mainStore.toast.setErrorText(
          "Something went wrong loading Assessment Templates",
        ),
      )
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <Loading loadingLayout="table" showTableHeader={false} />;
  }

  const onCopy = async (id: number) => {
    if (!workspaceID) {
      mainStore.toast.setErrorText("Copying the assessment template failed...");
      return;
    }

    mainStore.toast.setInfoText("Copying the assessment template...");
    try {
      const newAssessment = await AssessmentAPI.duplicateAssessment(
        workspaceID,
        id,
      );
      setAssessmentTemplates([newAssessment, ...assessmentTemplates]);
      mainStore.toast.setInfoText(
        "Assessment Template successfully duplicated!",
      );
    } catch {
      mainStore.toast.setErrorText(
        "An error has occurred. Please try again later.",
      );
    }
  };

  const onDelete = async (id: number) => {
    try {
      if (!workspaceID) {
        mainStore.toast.setErrorText(
          "Something went wrong, please try again...",
        );
        return;
      }
      await AssessmentAPI.delete(workspaceID, id);
      setAssessmentTemplates(assessmentTemplates.filter((a) => a.id != id));
    } catch {
      mainStore.toast.setErrorText(
        "An error has occurred. Please try again later.",
      );
    }
  };

  const headers = FIELDS.map((field) => (
    <ModuleTableColumn key={field.name} field={field} />
  ));

  const rows = assessmentTemplates.map((assessmentTemplate) => (
    <div key={assessmentTemplate.id} className="list-table">
      <ul>
        <div className="list-table-wrap">
          <div className="list-table-block">
            <CustomCell
              width={FIELDS[0].width}
              className="risk-area-table-listing__name"
            >
              <div
                onClick={() =>
                  history.push(
                    generatePath(ROUTES.TEMPLATE_EDIT_PATH, {
                      templateId: assessmentTemplate.id,
                      workspace_id: Number(workspaceID),
                    }),
                  )
                }
                style={{ pointerEvents: "auto", cursor: "pointer" }}
              >
                {assessmentTemplate.name}
              </div>
            </CustomCell>
            <CustomCell width={FIELDS[1].width}>
              <div>
                {getCountStringOfItem(
                  assessmentTemplate.questionnaire_ids.length,
                  "Template",
                )}
              </div>
            </CustomCell>
            <CustomCell width={FIELDS[2].width}>
              <div
                onClick={() =>
                  history.push(
                    generatePath(ROUTES.METHODOLOGY_EDIT_PATH, {
                      riskMethodologyId: assessmentTemplate.risk_methodology_id,
                      workspace_id: Number(workspaceID),
                    }),
                  )
                }
                style={{ pointerEvents: "auto", cursor: "pointer" }}
              >
                {
                  riskMethodologies.find(
                    (rm) => rm.id == assessmentTemplate.risk_methodology_id,
                  )?.name
                }
              </div>
            </CustomCell>
            <CustomCell
              width={FIELDS[3].width}
              // @ts-expect-error TS(2322) FIXME: Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
              value={formatDate(stringToDate(assessmentTemplate.created_at))}
            />
            <CustomCell width={FIELDS[4].width} allowClicks>
              <AssessmentTemplateButtons
                onCopy={() => onCopy(assessmentTemplate.id)}
                onDelete={() => onDelete(assessmentTemplate.id)}
              />
            </CustomCell>
            <span className="stretch-cell" />
          </div>
        </div>
      </ul>
    </div>
  ));

  return (
    <DashboardContent>
      <DashboardHeader
        title="Questionnaires"
        LeftActionBar={<ViewModuleUsers />}
        RightActionBar={<HeadSelect />}
      />
      <DashboardContentWrapper>
        <ModuleHeader>
          <RiskAssessmentTabs />
        </ModuleHeader>
        <div style={{ marginTop: 20 }}>
          <CustomTable
            headers={headers}
            rows={rows}
            createNewRecord={() => {
              history.push(
                generatePath(ROUTES.TEMPLATE_CREATION_PATH, {
                  workspace_id: Number(workspaceID),
                }),
              );
            }}
            addNewTemplateString="Add New Assessment Template +"
          />
        </div>
      </DashboardContentWrapper>
    </DashboardContent>
  );
}

export default observer(AssessmentTemplatesPage);
